// The run detail a reviewer reads (build plan P10): the request, what the applier would write,
// the gate check by check, the duplicate candidates and citations, and the drafted card with
// the fields a reviewer may change. Promote (with edits and recorded overrides), reject with a
// reason, or re-run with a note. Judged checks can be overridden; mechanical ones cannot.

import type { Me, TriageRunDetail } from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { ApiError } from "../api";
import { triageGet, triagePromote, triageReject, triageRerun } from "../triage-api";
import { age, reasonLabel } from "./StaffTriageQueue";

type Draft = Record<string, unknown>;

interface GateCheck {
  id: string;
  passed: boolean;
  confidence?: number;
  detail?: string;
}

function checksOf(gate: TriageRunDetail["applierGate"]): GateCheck[] {
  return gate?.checks ?? [];
}

function Field({
  label,
  value,
  rows,
  onChange,
}: {
  label: string;
  value: string;
  rows?: number;
  onChange: (v: string) => void;
}) {
  const id = `draft-${label.toLowerCase().replaceAll(/\W+/g, "-")}`;
  return (
    <div style={{ marginBottom: "0.75rem" }}>
      <label htmlFor={id}>{label}</label>
      <br />
      {rows ? (
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: "100%", fontFamily: "ui-monospace, monospace" }}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: "100%" }}
        />
      )}
    </div>
  );
}

export function StaffTriageRun({ me, runId }: { me: Me; runId: string }) {
  const [detail, setDetail] = useState<TriageRunDetail | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [releaseSummary, setReleaseSummary] = useState(true);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState("");
  const canDecide = me.user.roles.includes("staff") || me.user.roles.includes("admin");

  async function load() {
    try {
      const d = await triageGet(runId);
      setDetail(d);
      setDraft(structuredClone(d.proposal as Draft));
      setState("ready");
    } catch (error) {
      setState(error instanceof ApiError && error.failure.status === 404 ? "missing" : "error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
  }, [runId]);

  if (state === "loading") return <p role="status">Loading…</p>;
  if (state === "missing") return <p role="alert">No such run.</p>;
  if (state === "error" || !detail || !draft)
    return <p role="alert">The run could not be loaded.</p>;

  const reviewed = detail.reviewedAt !== null;
  const gate = detail.applierGate ?? detail.agentGate;
  const checks = checksOf(gate);
  const failingJudged = checks.filter((c) => !c.passed && c.confidence !== undefined);
  const failingMechanical = checks.filter((c) => !c.passed && c.confidence === undefined);
  const story = (draft.story ?? null) as Draft | null;
  const task = (draft.task ?? null) as Draft | null;

  function setStory(key: string, value: unknown) {
    setDraft((d) => (d ? { ...d, story: { ...(d.story as Draft), [key]: value } } : d));
  }
  function setTask(key: string, value: unknown) {
    setDraft((d) => (d ? { ...d, task: { ...(d.task as Draft), [key]: value } } : d));
  }
  function setChildTask(index: number, key: string, value: unknown) {
    setDraft((d) => {
      if (!d) return d;
      const s = d.story as Draft;
      const tasks = [...((s.childTasks as Draft[]) ?? [])];
      tasks[index] = { ...tasks[index], [key]: value };
      return { ...d, story: { ...s, childTasks: tasks } };
    });
  }

  async function promote() {
    if (!detail || !draft) return;
    const pending = failingJudged.filter((c) => !(overrides[c.id] ?? "").trim());
    if (pending.length > 0) {
      setNotice(`Give a reason for overriding: ${pending.map((c) => c.id).join(", ")}`);
      return;
    }
    if (
      failingMechanical.length > 0 &&
      !window.confirm(
        "A mechanical check is still failing; the applier will refuse this unless the draft now passes. Promote anyway?",
      )
    ) {
      return;
    }
    setNotice("Promoting…");
    try {
      const result = await triagePromote(me, detail.id, {
        proposal: draft,
        overrides: failingJudged.map((c) => ({ checkId: c.id, reason: overrides[c.id] ?? "" })),
        releaseSummary,
      });
      setNotice(
        result.changedPaths.length > 0
          ? `Promoted with edits to ${result.changedPaths.join(", ")}. The applier writes it next; check the card on the request.`
          : "Promoted as drafted. The applier writes it next; check the card on the request.",
      );
      await load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "Promotion failed.");
    }
  }

  async function reject() {
    if (!detail || reason.trim().length < 5) {
      setNotice("Give a reason of at least five characters.");
      return;
    }
    try {
      await triageReject(me, detail.id, { reason });
      setNotice("Rejected. The request stays in the queue with your reason on its thread.");
      await load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "Rejection failed.");
    }
  }

  async function rerun() {
    if (!detail || note.trim().length < 5) {
      setNotice("Give the agent a note of at least five characters.");
      return;
    }
    try {
      await triageRerun(me, detail.id, { note });
      setNotice("Re-run queued with your note as context.");
      await load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "Re-run failed.");
    }
  }

  return (
    <article aria-labelledby="run-heading">
      <p>
        <a href="/staff/triage">Back to triage</a> ·{" "}
        <a href={`/staff/requests/${detail.publicId}`}>Open the request</a>
      </p>
      <h2 id="run-heading">
        {detail.publicId}: {detail.subject}
      </h2>
      <p>
        <strong>{reasonLabel(detail)}</strong> · {detail.shape.replaceAll("_", " ")} ·{" "}
        {detail.category} ·{" "}
        {detail.confidence === null ? "" : `confidence ${Math.round(detail.confidence * 100)}% · `}
        {age(detail.ageMs)} old · {detail.behaviourSurfaceVersion}
        {reviewed ? ` · ${detail.reviewDecision} by ${detail.reviewerDisplayName ?? "staff"}` : ""}
        {detail.promotionRun ? (
          <>
            {" "}
            · promotion run:{" "}
            <a href={`/staff/triage/${detail.promotionRun.id}`}>
              {detail.promotionRun.outcome ?? "in progress"}
            </a>
          </>
        ) : null}
      </p>

      <details open>
        <summary>The request</summary>
        <p>
          {detail.request.requesterDisplayName ?? "Requester not yet signed in"} ·{" "}
          {detail.request.department} · {detail.request.location}
          {detail.request.affectedSystemOrDevice
            ? ` · ${detail.request.affectedSystemOrDevice}`
            : ""}
        </p>
        <pre style={{ whiteSpace: "pre-wrap" }}>{detail.request.description}</pre>
        {detail.request.errorText ? (
          <pre style={{ whiteSpace: "pre-wrap" }}>{detail.request.errorText}</pre>
        ) : null}
        {Object.keys(detail.request.followUpAnswers).length > 0 ? (
          <ul>
            {Object.entries(detail.request.followUpAnswers).map(([k, v]) => (
              <li key={k}>
                {k}: {String(v)}
              </li>
            ))}
          </ul>
        ) : null}
      </details>

      <details open>
        <summary>What the applier would write ({detail.preview.length} actions)</summary>
        <pre style={{ whiteSpace: "pre-wrap", background: "#f6f6f6", padding: "0.5rem" }}>
          {detail.preview.join("\n") || "(nothing: this run stopped before a plan)"}
        </pre>
      </details>

      <details open>
        <summary>
          Quality gate:{" "}
          {gate
            ? gate.passed
              ? "passed"
              : `${checks.filter((c) => !c.passed).length} failing`
            : "not run"}
        </summary>
        <ul>
          {checks.map((c) => (
            <li key={c.id} style={{ marginBottom: "0.5rem" }}>
              <strong>{c.passed ? "PASS" : "FAIL"}</strong> {c.id}
              {c.confidence !== undefined
                ? ` (judged, ${Math.round(c.confidence * 100)}%)`
                : " (mechanical)"}
              {c.detail ? ` — ${c.detail}` : ""}
              {!c.passed && c.confidence !== undefined && !reviewed && canDecide ? (
                <div>
                  <label htmlFor={`override-${c.id}`}>Override reason</label>
                  <br />
                  <input
                    id={`override-${c.id}`}
                    type="text"
                    value={overrides[c.id] ?? ""}
                    onChange={(e) => setOverrides((o) => ({ ...o, [c.id]: e.target.value }))}
                    style={{ width: "100%" }}
                  />
                </div>
              ) : null}
              {!c.passed && c.confidence === undefined ? (
                <div style={{ color: "#a4000f" }}>
                  Mechanical: cannot be overridden; fix the draft.
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </details>

      <details>
        <summary>Duplicate search and citations</summary>
        <pre style={{ whiteSpace: "pre-wrap" }}>
          {JSON.stringify(detail.duplicateDecision, null, 2)}
        </pre>
        <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(detail.citations, null, 2)}</pre>
      </details>

      {story ? (
        <section aria-labelledby="draft-heading">
          <h3 id="draft-heading">The drafted story</h3>
          <Field
            label="Title"
            value={String(story.title ?? "")}
            onChange={(v) => setStory("title", v)}
          />
          <Field
            label="Description"
            rows={12}
            value={String(story.description ?? "")}
            onChange={(v) => setStory("description", v)}
          />
          <Field
            label="Acceptance criteria"
            rows={6}
            value={String(story.acceptanceCriteria ?? "")}
            onChange={(v) => setStory("acceptanceCriteria", v)}
          />
          <Field
            label="Tags (comma separated)"
            value={((story.tags as string[]) ?? []).join(", ")}
            onChange={(v) =>
              setStory(
                "tags",
                v
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean),
              )
            }
          />
          <Field
            label="Priority (1-4, blank for none)"
            value={
              story.priority === undefined || story.priority === null ? "" : String(story.priority)
            }
            onChange={(v) => setStory("priority", v.trim() ? Number(v) : undefined)}
          />
          {((story.childTasks as Draft[]) ?? []).map((t, i) => (
            <fieldset key={String(t.title ?? i)} style={{ marginBottom: "0.75rem" }}>
              <legend>Child task {i + 1}</legend>
              <Field
                label={`Task ${i + 1} title`}
                value={String(t.title ?? "")}
                onChange={(v) => setChildTask(i, "title", v)}
              />
              <Field
                label={`Task ${i + 1} description`}
                rows={10}
                value={String(t.description ?? "")}
                onChange={(v) => setChildTask(i, "description", v)}
              />
            </fieldset>
          ))}
        </section>
      ) : null}
      {task ? (
        <section aria-labelledby="draft-task-heading">
          <h3 id="draft-task-heading">
            The drafted task (under #{String(draft.parentWorkItemId)})
          </h3>
          <Field
            label="Title"
            value={String(task.title ?? "")}
            onChange={(v) => setTask("title", v)}
          />
          <Field
            label="Description"
            rows={12}
            value={String(task.description ?? "")}
            onChange={(v) => setTask("description", v)}
          />
        </section>
      ) : null}
      {draft.shape === "reuse" ? (
        <section aria-labelledby="draft-reuse-heading">
          <h3 id="draft-reuse-heading">Reuse of #{String(draft.reuseWorkItemId)}</h3>
          <Field
            label="Note for the card"
            rows={4}
            value={String(draft.reuseNote ?? "")}
            onChange={(v) => setDraft((d) => (d ? { ...d, reuseNote: v || undefined } : d))}
          />
        </section>
      ) : null}

      <Field
        label="What the requester will read (released only after the write)"
        rows={3}
        value={String(draft.requesterSafeSummary ?? "")}
        onChange={(v) => setDraft((d) => (d ? { ...d, requesterSafeSummary: v } : d))}
      />

      {canDecide && !reviewed ? (
        <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr 1fr" }}>
          <div style={{ border: "1px solid #2a7", padding: "0.75rem" }}>
            <h3>Promote</h3>
            <label>
              <input
                type="checkbox"
                checked={releaseSummary}
                onChange={(e) => setReleaseSummary(e.target.checked)}
              />{" "}
              Release the summary to the requester
            </label>
            <br />
            <button
              type="button"
              onClick={() => void promote()}
              disabled={draft.shape === "not_executable" || draft.shape === "human_triage"}
            >
              Promote to the board
            </button>
            {draft.shape === "not_executable" || draft.shape === "human_triage" ? (
              <p style={{ fontSize: "0.85rem" }}>
                This run stopped without a draft; re-run with a note or handle it on the request.
              </p>
            ) : null}
          </div>
          <div style={{ border: "1px solid #a4000f", padding: "0.75rem" }}>
            <h3>Reject</h3>
            <label htmlFor="reject-reason">Reason</label>
            <textarea
              id="reject-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ width: "100%" }}
            />
            <button type="button" onClick={() => void reject()}>
              Reject the draft
            </button>
          </div>
          <div style={{ border: "1px solid #7a4b00", padding: "0.75rem" }}>
            <h3>Re-run</h3>
            <label htmlFor="rerun-note">Note for the agent</label>
            <textarea
              id="rerun-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ width: "100%" }}
            />
            <button type="button" onClick={() => void rerun()}>
              Re-run with this context
            </button>
          </div>
        </div>
      ) : null}
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </article>
  );
}
